import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  getAppRuntimeSnapshot,
  markServerUnavailable,
  probeServerConnection,
  setServerRetrying,
  subscribeAppRuntime,
} from '../../services/appRuntime';
import { ServerUnavailableDialog } from './ServerUnavailableDialog';

const AUTO_RETRY_MS = 15_000;
const OFFLINE_MESSAGE =
  'Connexion réseau indisponible. Vérifiez le Wi‑Fi ou les données mobiles, puis réessayez.';

export function AppRuntimeShell({ children }) {
  const runtime = useSyncExternalStore(subscribeAppRuntime, getAppRuntimeSnapshot);

  const handleRetry = useCallback(async () => {
    setServerRetrying(true);
    try {
      await probeServerConnection();
    } finally {
      setServerRetrying(false);
    }
  }, []);

  useEffect(() => {
    const onOffline = () => markServerUnavailable(null, OFFLINE_MESSAGE);
    const onOnline = () => {
      void probeServerConnection();
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      markServerUnavailable(null, OFFLINE_MESSAGE);
    }

    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  useEffect(() => {
    if (!runtime.serverUnavailable || runtime.retrying) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      void probeServerConnection();
    }, AUTO_RETRY_MS);

    return () => clearInterval(intervalId);
  }, [runtime.serverUnavailable, runtime.retrying]);

  return (
    <>
      {children}
      <ServerUnavailableDialog
        open={runtime.serverUnavailable}
        message={runtime.serverMessage}
        retrying={runtime.retrying}
        onRetry={handleRetry}
      />
    </>
  );
}
